'use client';

import { createContext, useContext, useState, type ComponentProps } from 'react';
import type * as TabsPrimitive from '@radix-ui/react-tabs';
import { twMerge } from 'tailwind-merge';
import { ButtonControls } from '../ButtonControls';

type ContextoDasAbas = {
	valor: string;
	definir: (valor: string) => void;
};

const AbasContext = createContext<ContextoDasAbas | null>(null);

const useAbas = () => {
	const ctx = useContext(AbasContext);

	if (!ctx) throw new Error('TabsList, TabsTrigger e TabsContent precisam estar dentro de Tabs');

	return ctx;
};

function Tabs({ value, defaultValue, onValueChange, className, children, orientation, activationMode, asChild, ...resto }: ComponentProps<typeof TabsPrimitive.Root>) {
	const [interno, setInterno] = useState(defaultValue ?? '');
	const valor = value ?? interno;

	const definir = (novo: string) => {
		if (value === undefined) setInterno(novo);
		onValueChange?.(novo);
	};

	return (
		<AbasContext.Provider value={{ valor, definir }}>
			<div data-slot='tabs' className={twMerge('flex flex-col gap-2', className)} {...resto}>
				{children}
			</div>
		</AbasContext.Provider>
	);
}

function TabsList({ className, children }: ComponentProps<typeof TabsPrimitive.List>) {
	const { valor, definir } = useAbas();

	return (
		<ButtonControls value={valor} onChange={(novo) => definir(String(novo))} className={className}>
			{children}
		</ButtonControls>
	);
}

function TabsTrigger({ value, className, children, disabled }: ComponentProps<typeof TabsPrimitive.Trigger>) {
	return (
		<ButtonControls.Item value={value} className={twMerge(disabled && 'pointer-events-none opacity-50', className)}>
			{children}
		</ButtonControls.Item>
	);
}

function TabsContent({ value, className, children, forceMount, asChild, ...resto }: ComponentProps<typeof TabsPrimitive.Content>) {
	const { valor } = useAbas();

	if (value !== valor && !forceMount) return null;

	return (
		<div data-slot='tabs-content' className={twMerge('flex-1 outline-none', className)} {...resto}>
			{children}
		</div>
	);
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
